import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  FileText, 
  Grid, 
  List,
  Upload
} from 'lucide-react';
import api, { getDocumentTypes } from '../services/api';
import { DocumentGrid, DocumentList, DocumentDetailView, DocumentEditModal } from '../components/DocumentComponents';
import { documentService } from '../services/documentService';
import toast from 'react-hot-toast';
import type { Document, DocumentsResponse, } from '../types/document';

export interface DocumentType {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
}

const Documents: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [documentTypeFilter, setDocumentTypeFilter] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [editingDocument, setEditingDocument] = useState<Document | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [favorites, setFavorites] = useState<Set<number>>(new Set());

  const [documentTypes, setDocumentTypes] = React.useState<DocumentType[]>([]);
  const [documentTypesLoading, setDocumentTypesLoading] = React.useState(false);

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const {
    data: documentsData,
    isLoading,
    error
  } = useQuery<DocumentsResponse>(
    ['documents', currentPage, searchTerm, documentTypeFilter],
    async () => {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        per_page: '12'
      });
      
      if (searchTerm) params.append('search', searchTerm);
      if (documentTypeFilter) params.append('document_type', documentTypeFilter);
      
      const response = await api.get(`/api/documents?${params}`);
      return response.data;
    },
    {
      keepPreviousData: true,
      staleTime: 300,
    }
  );

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  const handleDocumentClick = (document: Document) => {
    setSelectedDocument(document);
  };

  const handleEditDocument = (document: Document) => {
    setEditingDocument(document);
    setIsEditModalOpen(true);
  };

  const handleSaveDocument = async (updatedDoc: Partial<Document>) => {
    if (!editingDocument) return;
    
    try {
      await api.put(`/api/documents/${editingDocument.id}`, updatedDoc);
      
      // Update the selected document if it's the one being edited
      if (selectedDocument && selectedDocument.id === editingDocument.id) {
        setSelectedDocument({ ...editingDocument, ...updatedDoc });
      }
      
      // Refresh the documents list
      queryClient.invalidateQueries(['documents']);
      
      setIsEditModalOpen(false);
      setEditingDocument(null);
      
      console.log('Document updated successfully');
    } catch (error: any) {
      console.error('Error updating document:', error);
    }
  };

  const handleDeleteDocument = () => {
    // Refresh the documents list after deletion
    queryClient.invalidateQueries(['documents']);
  };

  useEffect(() => {
    console.log('use effect hook');
    // Load document types
    const loadData = async () => {
      setDocumentTypesLoading(true);
      try {
        const documentTypeData = await getDocumentTypes();
        setDocumentTypes(documentTypeData);
      } catch (error) {
        console.error('Failed to load document types:', error);
      } finally {
        setDocumentTypesLoading(false);
      }
    }
    loadData();

    // Load favorites
    const loadFavorites = async () => {
      try {
        const userFavorites = await documentService.getFavorites();
        const favoriteIds = new Set(userFavorites.map((fav: any) => fav.document.id));
        setFavorites(favoriteIds);
      } catch (error) {
        console.error('Failed to load favorites:', error);
      }
    };
    loadFavorites();
  }, []);

  const handleToggleFavorite = async (document: Document) => {
    try {
      const isFavorite = favorites.has(document.id);
      if (isFavorite) {
        await documentService.removeFromFavorites(document.id);
        const newFavorites = new Set(favorites);
        newFavorites.delete(document.id);
        setFavorites(newFavorites);
        toast.success('Removed from favorites');
      } else {
        await documentService.addToFavorites(document.id);
        const newFavorites = new Set(favorites);
        newFavorites.add(document.id);
        setFavorites(newFavorites);
        toast.success('Added to favorites');
      }
    } catch (error: any) {
      console.error('Failed to toggle favorite:', error);
      toast.error(error.message || 'Failed to update favorites');
    }
  };


  console.log(documentsData);

  return (
    <div className="p-6">
      {selectedDocument ? (
        <DocumentDetailView 
          document={selectedDocument} 
          onBack={() => setSelectedDocument(null)}
          onEdit={() => handleEditDocument(selectedDocument)}
          onDelete={() => {
            handleDeleteDocument();
            setSelectedDocument(null); // Go back to list after delete
          }}
        />
      ) : (
        <>
          {/* Header */}
        <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Documents
          </h1>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => navigate('/documents/upload')}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors mr-2 text-sm font-medium"
            >
              <Upload className="w-4 h-4" />
              <span>Document Upload</span>
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg ${viewMode === 'grid' 
                ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300' 
                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              <Grid className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg ${viewMode === 'list' 
                ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300' 
                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              <List className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search documents..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <select
                value={documentTypeFilter}
                onChange={(e) => setDocumentTypeFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                <option value="">All Types</option>
                {documentTypes.map(type => (
                  <option key={type.id} value={type.name}>{type.name}</option>
                ))}
              </select>
              <button
                type="submit"
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
              >
                Search
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
          <p className="text-red-600 dark:text-red-400">
            Failed to load documents. Please try again.
          </p>
        </div>
      )}

      {/* Documents Display */}
      {documentsData && (
        <div>
          {documentsData.documents.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                No documents found
              </h3>
              <p className="text-gray-500 dark:text-gray-400">
                {searchTerm || documentTypeFilter 
                  ? 'Try adjusting your search criteria.' 
                  : 'Upload your first document to get started.'}
              </p>
            </div>
          ) : (
            <div>
              {viewMode === 'grid' ? (
                <DocumentGrid 
                  documents={documentsData.documents} 
                  onDocumentClick={handleDocumentClick}
                  onEdit={handleEditDocument}
                  onDelete={handleDeleteDocument}
                  favorites={favorites}
                  onToggleFavorite={handleToggleFavorite}
                />
              ) : (
                <DocumentList 
                  documents={documentsData.documents} 
                  onDocumentClick={handleDocumentClick}
                  onEdit={handleEditDocument}
                  onDelete={handleDeleteDocument}
                  favorites={favorites}
                  onToggleFavorite={handleToggleFavorite}
                />
              )}
            </div>
          )}
        </div>
      )}
        </>
      )}

      {/* Edit Modal */}
      <DocumentEditModal
        document={editingDocument}
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingDocument(null);
        }}
        onSave={handleSaveDocument}
      />
    </div>
  );
};

export default Documents;
